import { vehicleTypesCategoryId } from '@/constants/common';
import type {
    BatteryPaymentMethod,
    BudgetRange,
    FinesInquiryType,
    InquiryPaymentMethod,
    InspectionPaymentMethod,
    VehicleType
} from '@/constants/dataLayer';
import {
    BATTERY_PAYMENT_METHODS,
    BUDGET_RANGES,
    FINES_INQUIRY_TYPES,
    INQUIRY_PAYMENT_METHODS,
    INSPECTION_PAYMENT_METHODS,
    VEHICLE_TYPES
} from '@/constants/dataLayer';
import { paymentGatewaysTypeId } from '@/constants/payment';
import { vehiclePlateCar, vehiclePlateMotorCycle } from '@/constants/plates';

const CAR_BUDGET_ID_MAP: Record<string, BudgetRange> = {
    'lt-1000': BUDGET_RANGES.UNDER_1B,
    '1000-2000': BUDGET_RANGES.FROM_1B_TO_2B,
    '2000-4000': BUDGET_RANGES.FROM_2B_TO_4B,
    '4000-7000': BUDGET_RANGES.FROM_4B_TO_7B,
    'gt-7000': BUDGET_RANGES.OVER_7B
};

const MOTOR_BUDGET_ID_MAP: Record<string, BudgetRange> = {
    'lt-300': BUDGET_RANGES.UNDER_1B,
    '300-600': BUDGET_RANGES.FROM_1B_TO_2B,
    '600-1000': BUDGET_RANGES.FROM_2B_TO_4B,
    '1000-1500': BUDGET_RANGES.FROM_4B_TO_7B,
    'gt-1500': BUDGET_RANGES.OVER_7B
};

export const mapCarBudgetIdToDataLayer = (budgetId: string): BudgetRange => CAR_BUDGET_ID_MAP[budgetId] ?? BUDGET_RANGES.UNDER_1B;

export const mapMotorBudgetIdToDataLayer = (budgetId: string): BudgetRange => MOTOR_BUDGET_ID_MAP[budgetId] ?? BUDGET_RANGES.UNDER_1B;

export const splitVehicleName = (name: string): { brand: string; model: string } => {
    const trimmed = name.trim();
    const spaceIndex = trimmed.indexOf(' ');

    if (spaceIndex === -1) {
        return { brand: trimmed, model: trimmed };
    }

    return {
        brand: trimmed.slice(0, spaceIndex),
        model: trimmed.slice(spaceIndex + 1).trim() || trimmed
    };
};

export const mapPlateVehicleTypeIdToDataLayer = (vehicleTypeId: number): VehicleType =>
    vehicleTypeId === vehiclePlateCar.id ? VEHICLE_TYPES.CAR : VEHICLE_TYPES.MOTORCYCLE;

export const mapApiVehicleTypeToDataLayer = (vehicleType?: string | number | null): VehicleType => {
    if (typeof vehicleType === 'number') {
        return mapPlateVehicleTypeIdToDataLayer(vehicleType);
    }

    const normalized = String(vehicleType || '').toUpperCase();
    if (normalized === 'CAR' || normalized === String(vehicleTypesCategoryId.CAR)) {
        return VEHICLE_TYPES.CAR;
    }

    if (normalized === 'MOTORCYCLE' || normalized === String(vehicleTypesCategoryId.MOTORCYCLE)) {
        return VEHICLE_TYPES.MOTORCYCLE;
    }

    if (Number(vehicleType) === vehiclePlateMotorCycle.id || vehicleType === '0') {
        return VEHICLE_TYPES.MOTORCYCLE;
    }

    return VEHICLE_TYPES.CAR;
};

export const mapInquiryTypeIdToDataLayer = (inquiryTypeId: number, detailedInquiryTypeId: number): FinesInquiryType =>
    inquiryTypeId === detailedInquiryTypeId ? FINES_INQUIRY_TYPES.ITEMIZED : FINES_INQUIRY_TYPES.FINAL_AMOUNT;

export const mapGatewayTypeToInquiryPaymentMethod = (gatewayType?: string): InquiryPaymentMethod => {
    switch (gatewayType) {
        case paymentGatewaysTypeId.digipay:
        case paymentGatewaysTypeId.azkivam:
            return INQUIRY_PAYMENT_METHODS.DIGIPAY_INSTALLMENT;
        case paymentGatewaysTypeId.blubank:
            return INQUIRY_PAYMENT_METHODS.BLUBANK_CREDIT;
        default:
            return INQUIRY_PAYMENT_METHODS.ONLINE;
    }
};

export const mapGatewayToInspectionPaymentMethod = (isInstalment?: boolean): InspectionPaymentMethod =>
    isInstalment ? INSPECTION_PAYMENT_METHODS.INSTALLMENT : INSPECTION_PAYMENT_METHODS.CASH;

export const mapGatewayToBatteryPaymentMethod = (isInstalment?: boolean): BatteryPaymentMethod =>
    isInstalment ? BATTERY_PAYMENT_METHODS.INSTALLMENT : BATTERY_PAYMENT_METHODS.CASH;

export const mapInspectionRouteToDataLayerVehicle = (vehicleType?: string | null): 'car' | 'motor' =>
    String(vehicleType || '').includes('motor') ? 'motor' : 'car';

export const mapBatteryRouteToDataLayerVehicle = (vehicleType?: string | null): 'car' | 'motor' =>
    String(vehicleType || '').includes('motor') ? 'motor' : 'car';
